/**
 * Vista por función — Sprint 286 Stream E; REQ-MINT-006 §2 / anexo §2.2.
 *
 * Para cada función del producto: requisitos y umbrales declarados, modelos
 * elegibles (mismos requisitos que el comparador), **uso real del periodo**
 * (telemetría A, por función y modelo) y **alternativas con delta** respecto a
 * un modelo de referencia. La vista no muta nada: solo informa y enlaza a la
 * configuración de modelos y a la ficha.
 *
 * Sin datos ⇒ «sin dato» explícito; el delta nunca se estima.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Gauge, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { PageHeader } from '@vanguard-atlas/shared';
import { MetricHint } from '../components/MetricHint';
import { ModelReferenceSelect } from '../components/ModelReferenceSelect';
import {
  useModelIntelCompare,
  useModelIntelFeedbackSummary,
  useModelIntelFunctionUsage,
  useModelIntelFunctions,
} from '../hooks/useModelIntel';
import { priceDeltaPct, scoreDelta, usageSummary } from '../lib/ficha';
import { summarizeExclusions } from '../lib/exclusions';
import { formatDate, formatPct, formatUsd } from '../lib/pricing';
import type { FunctionItem, OfferingIntelligence } from '../types';
import { useTenant } from "@/contexts/TenantContext";

function domainLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  domain: string,
): string {
  const key = `modelIntel.domains.${domain}`;
  const label = t(key);
  return label === key ? domain : label;
}

function priceInput(offering: OfferingIntelligence): number | null {
  const input = offering.client_price?.components?.['input']?.usd_per_million;
  if (offering.client_price?.state !== 'available' || typeof input !== 'number') return null;
  return input;
}

function domainScore(offering: OfferingIntelligence, domain: string): number | null {
  const found = offering.benchmarks?.domains?.find((item) => item.domain === domain);
  return typeof found?.score === 'number' ? found.score : null;
}

function FunctionList({ functions }: { functions: FunctionItem[] }) {
  const { t } = useI18n();
  if (functions.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="functions-empty">
        {t('modelIntel.functions.empty')}
      </p>
    );
  }
  return (
    <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {functions.map((fn) => (
        <li key={fn.code}>
          <Link
            to={`/model-intel/functions/${encodeURIComponent(fn.code)}`}
            className="block rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
            data-testid={`function-item-${fn.code}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{fn.name}</span>
              <span className="text-xs capitalize text-gray-500">{fn.state}</span>
            </div>
            <div className="mt-1 font-mono text-xs text-gray-500">{fn.code}</div>
            {fn.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{fn.description}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(fn.quality_thresholds?.['domain_min'] ?? {}).map(([domain, min]) => (
                <span
                  key={domain}
                  className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
                >
                  {domainLabel(t, domain)} ≥ {String(min)}
                </span>
              ))}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function FunctionDetail({ code }: { code: string }) {
  const { currentTenant } = useTenant();
  const configHref = currentTenant?.is_system_tenant
    ? "/settings/llm-catalog"
    : "/settings/llm-policy";
  const { t } = useI18n();
  const functions = useModelIntelFunctions();
  const compare = useModelIntelCompare();
  const [reference, setReference] = useState<string>('');

  const fn = functions.data?.items.find((item) => item.code === code) ?? null;

  const compareMutate = compare.mutate;
  const fnCode = fn?.code ?? null;
  const hasCompareData = compare.data !== undefined;
  const comparePending = compare.isPending;
  const compareFailed = compare.isError;
  useEffect(() => {
    if (fnCode && !hasCompareData && !comparePending && !compareFailed) {
      compareMutate({ function_code: fnCode, requirements: {} });
    }
  }, [fnCode, hasCompareData, comparePending, compareFailed, compareMutate]);

  const eligible = useMemo(() => compare.data?.eligible ?? [], [compare.data]);
  const referenceOptions = useMemo(
    () =>
      eligible
        .map((item) => item.offering.model_ref)
        .filter((ref): ref is string => typeof ref === 'string' && ref.length > 0),
    [eligible],
  );
  const feedback = useModelIntelFeedbackSummary(code);
  const usage = useModelIntelFunctionUsage(referenceOptions, code);

  const referenceOffering =
    eligible.find((item) => item.offering.model_ref === reference) ?? eligible[0] ?? null;
  const refPrice = referenceOffering ? priceInput(referenceOffering) : null;
  const refScore = referenceOffering?.scores?.weighted_score ?? null;
  const thresholds = Object.entries(fn?.quality_thresholds?.['domain_min'] ?? {});

  if (functions.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  if (!fn) {
    return (
      <div className="space-y-3 p-6" data-testid="function-not-found">
        <Link to="/model-intel/functions" className="inline-flex items-center gap-1 text-sm underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('modelIntel.functions.back')}
        </Link>
        <div className="rounded-md border p-4 text-sm">{t('modelIntel.functions.error')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid={`function-view-${fn.code}`}>
      <PageHeader
        icon={Gauge}
        title={fn.name}
        subtitle={
          <>
            <span className="font-mono">{fn.code}</span> · {fn.state}
          </>
        }
        actions={
          <Link
            to={configHref}
            className="inline-flex items-center gap-1 text-sm underline"
            data-testid="function-config-link"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            {t('modelIntel.functions.configLink')}
          </Link>
        }
        testId="function-header"
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-2 p-4" data-testid="function-definition">
            <div className="text-sm font-medium">{t('modelIntel.functions.definitionTitle')}</div>
            {fn.description && <p className="text-sm text-gray-600 dark:text-gray-300">{fn.description}</p>}
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('modelIntel.functions.requirements')}
            </div>
            <ul className="flex flex-wrap gap-1" data-testid="function-requirements">
              {(fn.required_capabilities ?? []).map((entry, index) => {
                const record = entry as Record<string, unknown>;
                return (
                  <li
                    key={`${index}-${Object.keys(record).join('-')}`}
                    className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
                  >
                    {Object.entries(record)
                      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                      .join(' · ')}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-2 p-4" data-testid="function-thresholds">
            <div className="text-sm font-medium">{t('modelIntel.functions.thresholdsTitle')}</div>
            {thresholds.length === 0 ? (
              <p className="text-sm text-gray-500">{t('modelIntel.functions.noData')}</p>
            ) : (
              <ul className="flex flex-wrap gap-1">
                {thresholds.map(([domain, min]) => (
                  <li
                    key={domain}
                    className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
                  >
                    {domainLabel(t, domain)} ≥ {String(min)}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 block text-xs text-gray-500 dark:text-gray-400">
              <span>{t('modelIntel.functions.reference')}</span>
              <ModelReferenceSelect
                options={referenceOptions}
                value={referenceOffering?.offering.model_ref ?? ''}
                onChange={setReference}
                label={t('modelIntel.functions.reference')}
                placeholder={t('modelIntel.functions.referenceSearch')}
                emptyLabel={t('modelIntel.functions.referenceEmpty')}
                testId="function-reference"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="mb-3 text-sm font-medium">{t('modelIntel.functions.resultsTitle')}</div>
          {compare.isPending && (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          )}
          {compare.isError && (
            <p className="text-sm" role="alert">
              {t('modelIntel.functions.error')}
            </p>
          )}
          {compare.data && eligible.length === 0 && (
            <div className="space-y-2" data-testid="function-no-eligible-block">
              <p className="text-sm text-gray-500" data-testid="function-no-eligible">
                {t('modelIntel.functions.noEligible')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="function-totals">
                {t('modelIntel.functions.totals', {
                  considered:
                    compare.data.totals?.considered ??
                    eligible.length + (compare.data.excluded?.length ?? 0),
                  eligible: compare.data.totals?.eligible ?? eligible.length,
                  excluded:
                    compare.data.totals?.excluded ??
                    compare.data.excluded?.length ??
                    0,
                })}
              </p>
              {(compare.data.excluded?.length ?? 0) > 0 && (
                <>
                  <p className="text-xs font-medium">
                    {t('modelIntel.functions.excludedTitle')}
                  </p>
                  <ul
                    className="list-disc space-y-1 pl-5 text-xs text-gray-500 dark:text-gray-400"
                    data-testid="function-excluded-reasons"
                  >
                    {summarizeExclusions(compare.data.excluded).map((reason) => (
                      <li key={reason.code}>
                        <span className="font-mono">{reason.code}</span> —{' '}
                        {reason.detail}{' '}
                        <span className="tabular-nums">({reason.count})</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          {eligible.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="function-results">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-3">{t('modelIntel.functions.colModel')}</th>
                    <th className="py-2 pr-3">{t('modelIntel.compare.colScore')}</th>
                    {thresholds.map(([domain]) => (
                      <th key={domain} className="py-2 pr-3">
                        {domainLabel(t, domain)}
                      </th>
                    ))}
                    <th className="py-2 pr-3">{t('modelIntel.compare.cost')}</th>
                    <th className="py-2 pr-3">
                      <MetricHint
                        label={t('modelIntel.functions.colDeltaCost')}
                        help={t('modelIntel.functions.deltaCostHelp')}
                        testId="delta-cost-help"
                      >
                        {t('modelIntel.functions.colDeltaCost')}
                      </MetricHint>
                    </th>
                    <th className="py-2 pr-3">
                      <MetricHint
                        label={t('modelIntel.functions.colDeltaScore')}
                        help={t('modelIntel.functions.deltaScoreHelp')}
                        testId="delta-score-help"
                      >
                        {t('modelIntel.functions.colDeltaScore')}
                      </MetricHint>
                    </th>
                    <th className="py-2 pr-3">{t('modelIntel.functions.usageTitle')}</th>
                    <th className="py-2">{t('modelIntel.compare.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {eligible.map((item) => {
                    const price = priceInput(item);
                    const score = item.scores?.weighted_score ?? null;
                    const isReference = item.offering.sheet_id === referenceOffering?.offering.sheet_id;
                    return (
                      <tr
                        key={item.offering.sheet_id}
                        className="border-b last:border-0"
                        data-testid={`function-row-${item.offering.sheet_id}`}
                      >
                        <td className="py-2 pr-3">
                          {item.offering.model_ref ?? item.offering.provider_model_id}
                          {isReference && (
                            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                              {t('modelIntel.functions.referenceBadge')}
                            </span>
                          )}
                          <div className="text-xs capitalize text-gray-500">
                            {item.offering.provider} · {item.offering.region}
                          </div>
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{score ?? '—'}</td>
                        {thresholds.map(([domain, min]) => {
                          const value = domainScore(item, domain);
                          const minNumber = typeof min === 'number' ? min : null;
                          const state =
                            value === null || minNumber === null
                              ? 'noData'
                              : value >= minNumber
                                ? 'ok'
                                : 'below';
                          return (
                            <td key={domain} className="py-2 pr-3">
                              <span
                                className={
                                  state === 'ok'
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : state === 'below'
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-gray-500'
                                }
                                data-testid={`threshold-${domain}-${item.offering.sheet_id}`}
                              >
                                {value !== null ? value.toFixed(0) : t('modelIntel.card.noData')}{' '}
                                {t(`modelIntel.functions.thresholdStates.${state}`)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="py-2 pr-3 tabular-nums">
                          {price !== null ? formatUsd(price) : t('modelIntel.compare.noData')}
                        </td>
                        <td
                          className="py-2 pr-3 tabular-nums"
                          data-testid={`function-usage-${item.offering.sheet_id}`}
                        >
                          {(() => {
                            const cells =
                              usage.data?.[item.offering.model_ref ?? ''] ?? [];
                            if (usage.isLoading) return '…';
                            if (cells.length === 0) {
                              return t('modelIntel.compare.noData');
                            }
                            const { tasks, cost, coverage } = usageSummary(cells);
                            return (
                              <div className="space-y-0.5">
                                <div>
                                  {t('modelIntel.functions.usageTasks', {
                                    count: tasks,
                                  })}
                                </div>
                                <div
                                  className="text-xs text-gray-500 dark:text-gray-400"
                                  title={t('modelIntel.platform.coverage', {
                                    value: Math.round(coverage * 100),
                                  })}
                                >
                                  {coverage > 0
                                    ? formatUsd(cost)
                                    : t('modelIntel.card.noData')}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {isReference ? (
                            '—'
                          ) : (
                            <MetricHint
                              label={t('modelIntel.functions.colDeltaCost')}
                              help={t('modelIntel.functions.deltaCostHelp')}
                              testId={`delta-cost-${item.offering.sheet_id}`}
                            >
                              {(() => {
                                const delta = priceDeltaPct(refPrice, price);
                                return delta !== null
                                  ? formatPct(delta)
                                  : t('modelIntel.compare.noData');
                              })()}
                            </MetricHint>
                          )}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {isReference ? (
                            '—'
                          ) : (
                            <MetricHint
                              label={t('modelIntel.functions.colDeltaScore')}
                              help={t('modelIntel.functions.deltaScoreHelp')}
                              testId={`delta-score-${item.offering.sheet_id}`}
                            >
                              {(() => {
                                const delta = scoreDelta(refScore, score);
                                return delta !== null
                                  ? `${delta > 0 ? '+' : ''}${delta}`
                                  : t('modelIntel.compare.noData');
                              })()}
                            </MetricHint>
                          )}
                        </td>
                        <td className="py-2">
                          {item.offering.model_ref && (
                            <Link
                              to={`/model-intel/model/${encodeURIComponent(item.offering.model_ref)}`}
                              className="text-sm underline"
                            >
                              {t('modelIntel.functions.openCard')}
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {compare.data && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {t('modelIntel.functions.provenance', {
                contract: compare.data.contract_version,
                date: formatDate(compare.data.generated_at),
              })}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4" data-testid="function-feedback">
          <div className="mb-2 text-sm font-medium">
            {t('modelIntel.functions.feedbackTitle')}
          </div>
          {feedback.isError ? (
            <p className="text-sm text-gray-500" data-testid="function-feedback-unavailable">
              {t('modelIntel.functions.feedbackUnavailable')}
            </p>
          ) : (feedback.data?.cells ?? []).filter((cell) => !cell.suppressed).length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="function-feedback-empty">
              {t('modelIntel.functions.feedbackEmpty')}
            </p>
          ) : (
            <>
              <ul className="space-y-1 text-sm" data-testid="function-feedback-cells">
                {(feedback.data?.cells ?? [])
                  .filter((cell) => !cell.suppressed)
                  .map((cell) => (
                    <li
                      key={`${cell.model_ref}-${cell.period_start ?? 'period'}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1"
                    >
                      <span className="font-medium">{cell.model_ref}</span>
                      <span className="tabular-nums">
                        {cell.satisfaction_mean !== null && cell.satisfaction_mean !== undefined
                          ? `${(cell.satisfaction_mean * 100).toFixed(0)} %`
                          : t('modelIntel.card.noData')}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('modelIntel.functions.feedbackResponses', { count: cell.n })}
                      </span>
                    </li>
                  ))}
              </ul>
              {(feedback.data?.suppressed_cells ?? 0) > 0 && (
                <p
                  className="mt-2 text-xs text-gray-500 dark:text-gray-400"
                  data-testid="function-feedback-suppressed"
                >
                  {t('modelIntel.functions.feedbackSuppressed', {
                    count: feedback.data?.suppressed_cells ?? 0,
                    k: feedback.data?.k_anonymity ?? 20,
                  })}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function FunctionViewPage() {
  const { t } = useI18n();
  const { code } = useParams();
  const functions = useModelIntelFunctions();

  if (code) return <FunctionDetail code={code} />;

  return (
    <div className="space-y-6 p-6" data-testid="model-intel-functions">
      <div className="flex items-center gap-3">
        <SlidersHorizontal className="h-6 w-6 text-primary" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-semibold">{t('modelIntel.functions.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('modelIntel.functions.subtitle')}
          </p>
        </div>
      </div>
      {functions.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : functions.isError ? (
        <div className="rounded-md border p-4 text-sm" role="alert">
          {t('modelIntel.functions.error')}
        </div>
      ) : (
        <FunctionList functions={functions.data?.items ?? []} />
      )}
    </div>
  );
}
